<template>
    <div class="bg-white shadow rounded-lg">
        <div class="flex p-4">
            <Link
                :href="documentLink"
                :target="target"
                class="flex items-center"
                @navigate="$emit('navigate', $event)"
            >
                <AssetCdnImage
                    :src="previewSrc"
                    :alt="document.filename"
                    :width="minMD ? 96 : 72"
                    :height="minMD ? 96 : 72"
                    object-fit="cover"
                    class="border border-slate-100 rounded-lg w-18 h-18 md:w-24 md:h-24"
                />
            </Link>
            <div class="flex w-full justify-between pl-4 md:pl-5 text-ellipsis overflow-hidden space-x-2">
                <div class="grow overflow-hidden">
                    <Link
                        :href="documentLink"
                        :target="target"
                        class="md:text-lg leading-6 md:leading-8 text-blue-600 mb-2 select-none line-clamp-2 break-words"
                        @navigate="$emit('navigate', $event)"
                    >
                        {{ document.filename }}
                    </Link>
                    <!-- subtitles - these are different in different contexts - -->
                    <span
                        v-if="shouldShowSubtitles"
                        class="subtitle text-sm md:text-base line-clamp-2 leading-5 md:leading-6 text-slate-600"
                    >
                        <span>{{ document['file_type_label'] }}</span>
                        <span class="mx-1">•</span>
                        <span v-if="shouldShowUserData">{{ t('results.item.from') }}</span>
                        <span v-if="shouldShowUserData && document['user_data'].id === null">
                            <span class="font-medium">{{ ' ' + document['user_data'].name + ' ' }}</span>
                            <span class="mx-1">•</span>
                        </span>

                        <span v-else-if="shouldShowUserData">
                            <a
                                :href="document['user_data'].link"
                                :target="target"
                                class="font-medium"
                                >{{ ' ' + document['user_data'].name + ' ' }}</a
                            >
                            <span
                                v-if="document['user_data'].karma_points !== null"
                                class="text-xs p-1 rounded-lg bg-purple-100 text-purple-700 mr-1"
                                >{{ document['user_data'].karma_points }}</span
                            >
                        </span>
                        <span>{{ ' ' + document['user_data'].time }}</span>
                    </span>
                    <span
                        v-if="shouldShowContext"
                        class="text-xs md:text-base line-clamp-2 leading-4 md:leading-6 text-slate-600"
                    >
                        <a
                            v-if="courseLink"
                            :href="courseLink"
                            class="text-slate-600 hover:underline hover:text-black"
                            v-text="document.course.name"
                        ></a>
                        <span v-else>{{ document.course_name }}</span>
                        <span class="mx-1">•</span>
                        <a
                            v-if="universityLink"
                            :href="universityLink"
                            class="text-slate-600 hover:underline hover:text-black"
                            v-text="document.course.university.name"
                        ></a>
                        <span v-else>{{ document.university_name }}</span>
                        <span class="mx-1">•</span>
                        <span>{{ document.file_type_label }}</span>
                        <span
                            v-if="shouldShowFollowTimestamp && document.followed_at"
                            class="mx-1"
                            >•</span
                        >
                        <span v-if="shouldShowFollowTimestamp && document.followed_at">{{
                            t('documents.results.subtitle.followed_label') + ' ' + document.followed_at
                        }}</span>
                    </span>
                </div>
                <div class="flex-none">
                    <TagAI
                        v-if="document.has_ai_content"
                        v-tooltip="{
                            content: t('tooltip.aiTag'),
                            placement: 'left',
                        }"
                        data-testid="ai-extended-tag"
                        v-bind="screenSize !== 'small' ? { text: t('tags.aiText') } : undefined"
                    ></TagAI>
                </div>
            </div>
        </div>
        <hr class="mx-4" />
        <TagList
            class="justify-end px-4 py-2"
            tag-size="sm"
            tag-bare
            :tags="getStats"
        />
    </div>
</template>

<script>
import route from '@router/route';
import { mapGetters } from 'vuex';
import TagAI from '@components/TagAi';
import { convertToReadableNumber } from '@/helpers/string';
import { useTranslation } from 'i18next-vue';
import { useScreenSize } from '@/composables/screenSize';

export default {
    name: 'DocumentListItem',
    components: { TagAI },
    props: {
        document: {
            type: Object,
            required: true,
        },
        context: {
            type: String,
            required: false,
            validator(value) {
                return (
                    [
                        'course-page',
                        'search-page',
                        'university-page',
                        'profile-page',
                        'study-material-page',
                        'study-material-followed-page',
                    ].indexOf(value) !== -1
                );
            },
            default: () => {
                return 'course';
            },
        },
        target: {
            type: String,
            default: '_self',
            validator(value) {
                return ['_self', '_blank'].includes(value);
            },
        },
    },
    emits: ['navigate'],
    setup() {
        const { t } = useTranslation('course');
        const { minMD } = useScreenSize();
        return {
            t,
            minMD,
        };
    },
    computed: {
        ...mapGetters('ui', ['screenSize', 'getLocale']),
        lang() {
            return this.getLocale.slice(0, 2);
        },
        shouldShowContext() {
            return (
                [
                    'search-page',
                    'profile-page',
                    'study-material-page',
                    'study-material-followed-page',
                ].indexOf(this.context) !== -1
            );
        },
        shouldShowFollowTimestamp() {
            return ['study-material-followed-page'].indexOf(this.context) !== -1;
        },
        shouldShowSubtitles() {
            return ['course-page'].indexOf(this.context) !== -1;
        },
        shouldShowUserData() {
            return ['profile-page'].indexOf(this.context) === -1;
        },
        documentLink() {
            const link = this.document.file_link;
            if (link && link.trim() !== '') {
                return link;
            }
            return this.route('document.show', {
                slug: this.document.file_name_shorten,
                id: this.document.file_id,
            });
        },
        courseLink() {
            return this.document?.course?.id
                ? this.route('course.show', {
                      courseName: this.document.course.slug,
                      id: this.document.course.id,
                  })
                : false;
        },
        universityLink() {
            return this.document?.course?.university?.id
                ? this.route('university.show', {
                      uniName: this.document.course.university.name,
                      id: this.document.course.university.id,
                  })
                : false;
        },
        userLiked() {
            return this.document?.user_upvoted ?? false;
        },
        getStats() {
            return [
                {
                    icon: this.document.user_rated ? 'star-full' : 'star',
                    value: `
                        <span>${this.document.avg_star_score}
                            <span class="text-slate-400">(${this.convertToReadableNumber(
                                this.document.ratings_count,
                            )})</span>
                        </span>`,
                    iconClass: this.document.user_rated ? 'text-yellow-300' : '',
                },
                { icon: 'discussions', value: this.document.number_questions },
                { icon: 'downloads', value: this.document.downloads },
            ];
        },
        previewSrc() {
            return this.document.file_preview_link ?? this.document.file_preview_thumb_link;
        },
    },
    methods: {
        route,
        convertToReadableNumber,
    },
};
</script>

<style lang="scss" scoped>
.subtitle {
    /* Needed to override style in the clamp plugin */
    word-break: break-word !important;
}
</style>
